import React from "react";
import { useForm } from "react-hook-form";
import { motion } from "framer-motion";
import Swal from "sweetalert2";
import {
  FiFileText,
  FiMessageSquare,
  FiCalendar,
  FiSave,
  FiArrowRight,
  FiShield,
  FiCheckCircle,
  FiAlertCircle,
  FiEye
} from "react-icons/fi";
import {
  FaNewspaper,
  FaRegCalendarAlt,
  FaRegFileAlt
} from "react-icons/fa";

const AddNews = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch
  } = useForm();

  const watchedFields = watch();

  const onSubmit = async (data) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/create_news`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      const responseData = await res.json();

      if (res.ok) {
        Swal.fire({
          icon: "success",
          title: "News Added Successfully!",
          text: responseData.message || "The news was added successfully!",
          confirmButtonColor: "#6366f1",
          timer: 2000,
          timerProgressBar: true,
          background: "#ffffff"
        });
        reset();
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: responseData.message || "Something went wrong while adding news.",
          confirmButtonColor: "#ef4444",
        });
      }
    } catch (error) {
      console.error("News submission error:", error);
      Swal.fire({
        icon: "error",
        title: "Network Error",
        text: "Failed to submit news. Please try again later.",
        confirmButtonColor: "#ef4444",
      });
    }
  };

  const inputClasses = "w-full px-4 py-3 rounded-xl border transition-all duration-200 outline-none bg-gray-50/50 focus:bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200";
  const labelClasses = "block text-sm font-semibold text-gray-700 mb-2";
  const errorClasses = "text-xs text-red-500 mt-1 flex items-center gap-1";

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-indigo-50/30 to-purple-50/30 py-6 sm:py-10 flex items-center justify-center">
      <div className="container mx-auto px-4 max-w-2xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-5">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-xl">
                <FaNewspaper className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-white font-semibold text-xl">Add News</h2>
                <p className="text-indigo-100 text-sm mt-0.5">Add a new news article or announcement</p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
            {/* Title Field */}
            <div>
              <label className={labelClasses}>
                News Title <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <FiFileText className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  {...register("title", {
                    required: "Title is required",
                    maxLength: { value: 100, message: "Title must be less than 100 characters" }
                  })}
                  className={`${inputClasses} pl-10 ${errors.title ? 'border-red-500' : ''}`}
                  placeholder="Enter news title"
                />
              </div>
              {errors.title && (
                <p className={errorClasses}>
                  <FiAlertCircle className="w-3 h-3" />
                  {errors.title.message}
                </p>
              )}
            </div>

            {/* Description Field */}
            <div>
              <label className={labelClasses}>
                Description <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <FiMessageSquare className="absolute left-3 top-3 text-gray-400 w-4 h-4" />
                <textarea
                  {...register("description", {
                    required: "Description is required",
                    minLength: { value: 5, message: "Description must be at least 5 characters" }
                  })}
                  rows={4}
                  className={`${inputClasses} pl-10 pt-3 resize-none ${errors.description ? 'border-red-500' : ''}`}
                  placeholder="Enter news description..."
                />
              </div>
              {errors.description && (
                <p className={errorClasses}>
                  <FiAlertCircle className="w-3 h-3" />
                  {errors.description.message}
                </p>
              )}
            </div>

            {/* Date Field */}
            <div>
              <label className={labelClasses}>
                News Date <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <FiCalendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="date"
                  {...register("date", { required: "Date is required" })}
                  className={`${inputClasses} pl-10 ${errors.date ? 'border-red-500' : ''}`}
                />
              </div>
              {errors.date && (
                <p className={errorClasses}>
                  <FiAlertCircle className="w-3 h-3" />
                  {errors.date.message}
                </p>
              )}
            </div>

            {/* Preview Section */}
            {(watchedFields.title || watchedFields.description || watchedFields.date) && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-100"
              >
                <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <FiEye className="w-4 h-4 text-indigo-600" />
                  Preview
                </h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Title:</span>
                    <span className="text-sm font-medium text-gray-800">{watchedFields.title || '—'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Description:</span>
                    <span className="text-sm text-gray-600 line-clamp-2">{watchedFields.description || '—'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Date:</span>
                    <span className="text-sm font-medium text-gray-800">
                      {watchedFields.date ? new Date(watchedFields.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'}
                    </span>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Submit Button */}
            <div className="pt-2">
              <button
                type="submit"
                className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 rounded-xl font-semibold hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2"
              >
                <FiSave className="w-4 h-4" />
                Publish News
                <FiArrowRight className="w-4 h-4" />
              </button>
            </div>

            {/* Security Note */}
            <div className="text-center pt-2">
              <p className="text-xs text-gray-400 flex items-center justify-center gap-1">
                <FiShield className="w-3 h-3" />
                News will be visible to all users after publishing
              </p>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default AddNews;